import { ref, computed, type Ref } from 'vue';
import type {
  FetchOrganisationResponse,
  FetchUnitResponse,
  FetchSiteResponse,
  FetchOrganisationContactPersonResponse,
} from 'src/api/models';
import { useTicketService } from 'src/services/ticket-service';
import { useOrganisationService } from 'src/services/organisation-service';
import { useQueryClient } from '@tanstack/vue-query';
import { getGetFetchUnitQueryOptions } from 'src/api/queries/order-portal-bff';
import { useSiteService } from 'src/services/site-service';
import type { EntityMismatch } from './types';

export interface UseTicketEntityStateOptions {
  ticketId?: Ref<string | undefined>;
}

export interface UseTicketEntityStateReturn {
  // Entity state
  organisation: Ref<FetchOrganisationResponse | undefined>;
  unit: Ref<FetchUnitResponse | undefined>;
  site: Ref<FetchSiteResponse | undefined>;
  contactPerson: Ref<FetchOrganisationContactPersonResponse | undefined>;
  unitContactPerson: Ref<FetchOrganisationContactPersonResponse | undefined>;

  // Mismatch state
  unitMismatch: Ref<EntityMismatch | undefined>;
  siteMismatch: Ref<EntityMismatch | undefined>;
  contactPersonMismatch: Ref<EntityMismatch | undefined>;
  unitContactPersonMismatch: Ref<EntityMismatch | undefined>;

  // Loading
  isInitializing: Ref<boolean>;

  // Computed
  organisationId: Ref<string | undefined>;
  hasAnyMismatch: Ref<boolean>;

  // Actions
  setOrganisation: (org: FetchOrganisationResponse | undefined) => Promise<void>;
  setUnit: (unit: FetchUnitResponse | undefined) => Promise<void>;
  setSite: (site: FetchSiteResponse | undefined) => Promise<void>;
  setContactPerson: (contact: FetchOrganisationContactPersonResponse | undefined) => Promise<void>;
  setUnitContactPerson: (contact: FetchOrganisationContactPersonResponse | undefined) => Promise<void>;

  // Initialization
  initializeFromTicket: (ticket: any) => Promise<void>;
  reset: () => void;
}

export function useTicketEntityState(
  options: UseTicketEntityStateOptions = {}
): UseTicketEntityStateReturn {
  const { ticketId } = options;

  // Services
  const ticketService = useTicketService();
  const organisationService = useOrganisationService();
  const queryClient = useQueryClient();
  const siteService = useSiteService();

  // Entity state
  const organisation = ref<FetchOrganisationResponse | undefined>(undefined);
  const unit = ref<FetchUnitResponse | undefined>(undefined);
  const site = ref<FetchSiteResponse | undefined>(undefined);
  const contactPerson = ref<FetchOrganisationContactPersonResponse | undefined>(undefined);
  const unitContactPerson = ref<FetchOrganisationContactPersonResponse | undefined>(undefined);

  // Mismatch state
  const unitMismatch = ref<EntityMismatch | undefined>(undefined);
  const siteMismatch = ref<EntityMismatch | undefined>(undefined);
  const contactPersonMismatch = ref<EntityMismatch | undefined>(undefined);
  const unitContactPersonMismatch = ref<EntityMismatch | undefined>(undefined);

  // Loading state
  const isInitializing = ref(false);

  // Computed
  const organisationId = computed(() => organisation.value?.id);

  const hasAnyMismatch = computed(() => {
    return !!(
      unitMismatch.value ||
      siteMismatch.value ||
      contactPersonMismatch.value ||
      unitContactPersonMismatch.value
    );
  });

  // Helper: Check if entity belongs to a different organisation
  function checkMismatch(
    entityType: 'unit' | 'site' | 'contact_person',
    entityId: string,
    entityName: string,
    entityOrgId: string | undefined,
    entityOrgName: string | undefined
  ): EntityMismatch | undefined {
    // No mismatch if no organisation is selected
    if (!organisation.value?.id) {
      return undefined;
    }

    // No mismatch if entity has no organisation
    if (!entityOrgId) {
      return undefined;
    }

    // No mismatch if organisations match
    if (entityOrgId === organisation.value.id) {
      return undefined;
    }

    // Mismatch detected
    return {
      entityType,
      entityId,
      entityName,
      belongsToOrganisation: {
        id: entityOrgId,
        name: entityOrgName || 'Unknown',
      },
      currentOrganisation: {
        id: organisation.value.id,
        name: organisation.value.name || 'Unknown',
      },
    };
  }

  // Helper: Make API call if ticketId is provided
  async function updateTicketIfNeeded(
    updateFn: (ticketId: string, entityId: string | undefined) => Promise<any>,
    entityId: string | undefined
  ): Promise<void> {
    if (ticketId?.value) {
      await updateFn(ticketId.value, entityId);
    }
  }

  // Re-evaluate mismatches when organisation changes
  function reevaluateMismatches(): void {
    // Re-check unit mismatch
    if (unit.value?.id) {
      unitMismatch.value = checkMismatch(
        'unit',
        unit.value.id,
        unit.value.name || `#${unit.value.fleet_number}`,
        unit.value.organisation?.id,
        unit.value.organisation?.name
      );
    } else {
      unitMismatch.value = undefined;
    }

    // Re-check contact person mismatch
    if (contactPerson.value?.id) {
      contactPersonMismatch.value = checkMismatch(
        'contact_person',
        contactPerson.value.id,
        `${contactPerson.value.first_name} ${contactPerson.value.last_name}`,
        contactPerson.value.organisation?.id,
        contactPerson.value.organisation?.name
      );
    } else {
      contactPersonMismatch.value = undefined;
    }

    // Re-check unit contact person mismatch
    if (unitContactPerson.value?.id) {
      unitContactPersonMismatch.value = checkMismatch(
        'contact_person',
        unitContactPerson.value.id,
        `${unitContactPerson.value.first_name} ${unitContactPerson.value.last_name}`,
        unitContactPerson.value.organisation?.id,
        unitContactPerson.value.organisation?.name
      );
    } else {
      unitContactPersonMismatch.value = undefined;
    }

    // Site mismatch is harder to track since sites don't have direct org reference
    // We'll clear it when org changes and let it be set when site is selected
    siteMismatch.value = undefined;
  }

  // Set organisation
  async function setOrganisation(org: FetchOrganisationResponse | undefined): Promise<void> {
    organisation.value = org;

    // Re-evaluate all mismatches
    reevaluateMismatches();

    // Update ticket if in edit mode
    await updateTicketIfNeeded(
      ticketService.updateTicketOrganisation,
      org?.id
    );
  }

  // Set unit
  async function setUnit(newUnit: FetchUnitResponse | undefined): Promise<void> {
    // If no unit, clear state
    if (!newUnit) {
      unit.value = undefined;
      unitMismatch.value = undefined;
      await updateTicketIfNeeded(ticketService.updateTicketUnit, undefined);
      return;
    }

    // Fetch full unit data if we only have partial
    let fullUnit = newUnit;
    if (newUnit.id && !newUnit.organisation) {
      try {
        const { queryKey, queryFn } = getGetFetchUnitQueryOptions(newUnit.id);
        fullUnit = await queryClient.fetchQuery({ queryKey, queryFn });
      } catch {
        // Use partial data if fetch fails
      }
    }

    unit.value = fullUnit;

    // Check for mismatch
    unitMismatch.value = checkMismatch(
      'unit',
      fullUnit.id || '',
      fullUnit.name || `#${fullUnit.fleet_number}`,
      fullUnit.organisation?.id,
      fullUnit.organisation?.name
    );

    // If no organisation selected and unit has one, auto-set it
    if (!organisation.value && fullUnit.organisation?.id) {
      try {
        const fullOrg = await organisationService.fetchOrganisation(fullUnit.organisation.id);
        organisation.value = fullOrg;
        unitMismatch.value = undefined; // No mismatch since we just set the org
      } catch {
        // Set partial org data if fetch fails
        organisation.value = fullUnit.organisation as FetchOrganisationResponse;
        unitMismatch.value = undefined;
      }
    }

    // Update ticket if in edit mode
    await updateTicketIfNeeded(ticketService.updateTicketUnit, fullUnit.id);
  }

  // Set site
  async function setSite(newSite: FetchSiteResponse | undefined): Promise<void> {
    site.value = newSite;

    // Sites don't have direct organisation reference, so we can't check mismatch
    // The mismatch is determined by how the site was fetched (related vs unrelated)
    siteMismatch.value = undefined;

    // Update ticket if in edit mode
    await updateTicketIfNeeded(ticketService.updateTicketSite, newSite?.id);
  }

  // Set contact person
  async function setContactPerson(
    contact: FetchOrganisationContactPersonResponse | undefined
  ): Promise<void> {
    contactPerson.value = contact;

    if (!contact) {
      contactPersonMismatch.value = undefined;
      await updateTicketIfNeeded(ticketService.updateTicketOrganisationContactPerson, undefined);
      return;
    }

    // Check for mismatch
    contactPersonMismatch.value = checkMismatch(
      'contact_person',
      contact.id || '',
      `${contact.first_name} ${contact.last_name}`,
      contact.organisation?.id,
      contact.organisation?.name
    );

    // If no organisation selected and contact has one, auto-set it
    if (!organisation.value && contact.organisation?.id) {
      try {
        const fullOrg = await organisationService.fetchOrganisation(contact.organisation.id);
        organisation.value = fullOrg;
        contactPersonMismatch.value = undefined;
      } catch {
        organisation.value = contact.organisation as FetchOrganisationResponse;
        contactPersonMismatch.value = undefined;
      }
    }

    // Update ticket if in edit mode
    await updateTicketIfNeeded(ticketService.updateTicketOrganisationContactPerson, contact.id);
  }

  // Set unit contact person
  async function setUnitContactPerson(
    contact: FetchOrganisationContactPersonResponse | undefined
  ): Promise<void> {
    unitContactPerson.value = contact;

    if (!contact) {
      unitContactPersonMismatch.value = undefined;
      await updateTicketIfNeeded(ticketService.updateTicketContactSite, undefined);
      return;
    }

    // Check for mismatch
    unitContactPersonMismatch.value = checkMismatch(
      'contact_person',
      contact.id || '',
      `${contact.first_name} ${contact.last_name}`,
      contact.organisation?.id,
      contact.organisation?.name
    );

    // Update ticket if in edit mode
    await updateTicketIfNeeded(ticketService.updateTicketContactSite, contact.id);
  }

  // Initialize from existing ticket data
  async function initializeFromTicket(ticket: any): Promise<void> {
    isInitializing.value = true;
    // Set organisation first (primary entity)
    if (ticket.organisation?.id) {
      try {
        const org = await organisationService.fetchOrganisation(ticket.organisation?.id);
        organisation.value = org;
      } catch {
        organisation.value = undefined;
      }
    }

    // Set unit - fetch full data since ticket.unit only contains { id }
    if (ticket.unit?.id) {
      try {
        const { queryKey, queryFn } = getGetFetchUnitQueryOptions(ticket.unit.id);
        const fullUnit = await queryClient.fetchQuery({ queryKey, queryFn });
        unit.value = fullUnit;
        // Check mismatch after setting
        if (fullUnit?.id) {
          unitMismatch.value = checkMismatch(
            'unit',
            fullUnit.id,
            fullUnit.name || `#${fullUnit.fleet_number}`,
            fullUnit.organisation?.id,
            fullUnit.organisation?.name
          );
        }
      } catch {
        unit.value = undefined;
      }
    }

    // Set site - fetch full data since ticket.site may have partial data
    if (ticket.site?.id) {
      try {
        const fullSite = await siteService.fetchSite(ticket.site.id);
        site.value = fullSite;
      } catch {
        site.value = undefined;
      }
    }

    // Extract contact persons from the contact_persons array
    // The array contains objects with a 'reference' field indicating the type
    const organisationContactPersons = ticket.organisation_contact_persons || [];

    if (organisationContactPersons.length > 0) {
      contactPerson.value = organisationContactPersons[0];
      // Note: NestedTicketContractPerson doesn't include organisation data,
      // so mismatch detection won't work on initial load (which is fine)
    }

    const siteContactPersons = ticket.site_contact_persons || [];

    // Find site contact person
    if (siteContactPersons.length > 0) {
      unitContactPerson.value = siteContactPersons[0];
    }
    isInitializing.value = false;
  }

  // Reset all state
  function reset(): void {
    organisation.value = undefined;
    unit.value = undefined;
    site.value = undefined;
    contactPerson.value = undefined;
    unitContactPerson.value = undefined;
    unitMismatch.value = undefined;
    siteMismatch.value = undefined;
    contactPersonMismatch.value = undefined;
    unitContactPersonMismatch.value = undefined;
  }

  return {
    // Entity state
    organisation,
    unit,
    site,
    contactPerson,
    unitContactPerson,

    // Mismatch state
    unitMismatch,
    siteMismatch,
    contactPersonMismatch,
    unitContactPersonMismatch,

    // Loading
    isInitializing,

    // Computed
    organisationId,
    hasAnyMismatch,

    // Actions
    setOrganisation,
    setUnit,
    setSite,
    setContactPerson,
    setUnitContactPerson,

    // Initialization
    initializeFromTicket,
    reset,
  };
}
